import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Stamp, Loader2, Eye } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Switch } from "../ui/switch";

const MODES = [{ v: "both", l: "Logo + names" }, { v: "names", l: "Names only" }, { v: "logo", l: "Logo only" }];

export function WatermarkCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [img, setImg] = useState("");
  const [saved, setSaved] = useState(null);
  useEffect(() => { api.get("/weddings/mine/watermark").then((r) => { setS(r.data); setSaved(r.data); }).catch(() => {}); }, []);
  useEffect(() => () => img && URL.revokeObjectURL(img), [img]);
  if (!s) return null;
  const body = () => ({ enabled: s.enabled, mode: s.mode, text: s.text === s.default_text ? "" : s.text, position: s.position, opacity: s.opacity });
  async function save(patch, base = s) {
    const next = { ...base, ...patch };
    setS({ ...s, ...patch }); setBusy("save");
    try {
      await api.put("/weddings/mine/watermark", { enabled: next.enabled, mode: next.mode, text: next.text === s.default_text ? "" : next.text, position: next.position, opacity: next.opacity });
      setSaved(next);
      toast.success(next.enabled ? "Watermark is on for your photos" : "Watermark turned off");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  }
  async function preview() {
    setBusy("preview");
    try {
      const { data } = await api.post("/weddings/mine/watermark/preview", body(), { responseType: "blob" });
      setImg(URL.createObjectURL(data));
    } catch (e) {
      if (e.response?.data instanceof Blob) { try { e.response.data = JSON.parse(await e.response.data.text()); } catch { e.response.data = {}; } }
      toast.error(formatApiError(e));
    } finally { setBusy(""); }
  }
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-5 mb-6" data-testid="watermark-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3"><Stamp className="w-5 h-5 text-[var(--gold)]" /><div><p className="font-serif text-xl">Photo watermark</p><p className="text-xs text-[var(--muted)]">Adds a small mark to the corner of every photo people see or download.</p></div></div>
        <label className="flex items-center gap-2 text-sm cursor-pointer"><Switch checked={s.enabled} onCheckedChange={(v) => save({ enabled: v }, saved)} data-testid="watermark-toggle" /> {s.enabled ? "On" : "Off"}</label>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {MODES.map((m) => <button key={m.v} onClick={() => setS({ ...s, mode: m.v })} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${s.mode === m.v ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)] hover:border-[var(--gold)]"}`} data-testid={`watermark-mode-${m.v}`}>{m.l}</button>)}
        {s.mode !== "logo" && <input value={s.text} onChange={(e) => setS({ ...s, text: e.target.value })} maxLength={60} placeholder={s.default_text || "Your names · #hashtag"} className="input-luxe text-sm flex-1 min-w-[200px]" data-testid="watermark-text" />}
        <button onClick={preview} disabled={!!busy} className="btn-outline-gold text-xs" data-testid="watermark-preview">{busy === "preview" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} Preview</button>
        <button onClick={() => save({})} disabled={!!busy} className="btn-gold text-xs" data-testid="watermark-save">{busy === "save" ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Save</button>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-6" data-testid="watermark-placement">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-[var(--muted)] mb-1.5">Corner</p>
          <div className="grid grid-cols-2 gap-1.5 w-28 h-24 p-1.5 rounded-lg border border-[var(--line)] bg-[var(--bg-warm)]">
            {["top-left", "top-right", "bottom-left", "bottom-right"].map((p) => (
              <button key={p} onClick={() => setS({ ...s, position: p })} aria-label={p.replace("-", " ")} className={`rounded transition-colors ${s.position === p ? "bg-[var(--gold)]" : "bg-white hover:bg-[var(--gold)]/40"}`} data-testid={`watermark-pos-${p}`} />
            ))}
          </div>
        </div>
        <label className="flex-1 min-w-[200px]">
          <span className="text-[11px] uppercase tracking-wider text-[var(--muted)]">How solid · {s.opacity}%</span>
          <input type="range" min={20} max={100} step={5} value={s.opacity} onChange={(e) => setS({ ...s, opacity: Number(e.target.value) })} className="w-full accent-[var(--gold)]" data-testid="watermark-opacity" />
          <span className="flex justify-between text-[10px] text-[var(--muted)]"><span>See-through</span><span>Solid</span></span>
        </label>
      </div>
      {img && <img src={img} alt="Watermark preview" className="mt-4 rounded-xl max-h-80 w-auto border border-[var(--line)]" data-testid="watermark-preview-img" />}
    </div>
  );
}
